// react
import { useContext, useMemo, useState } from 'react';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { Link as RouterLink } from 'react-router-dom';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import {
	Alert,
	Box,
	Button,
	Grid,
	InputAdornment,
	InputLabel,
	Link,
	Typography,
} from '@mui/material';

// icons
import { AddOutlined as IconAdd, DeleteOutlined as IconDelete } from '@mui/icons-material';

// types
import {
	AllocationPayload,
	CategoryAllocationPayloadPending,
	ComponentAllocationPayloadPending,
	ConversionPayload,
	ExchangeRateByDate,
	Loan,
} from '../../../@types';

// fields
import { ExchangeRates } from '../ExchangeRates/ExchangeRates';
import { Input } from '../Input/Input';
import { Select } from '../Select/Select';

// theme
import { theme } from '../../../theme';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface AllocationRow {
	amount: number;
	conversions: ConversionPayload[];
	currency: string;
	loan: null | number;
}

export interface AllocationsProps {
	allocations: AllocationPayload[];
	exchangeRates: ExchangeRateByDate[];
	idForm: string;
	onChange: (allocations: AllocationPayload[]) => void;
	onChangeExchangeRates: (exchangeRates: ExchangeRateByDate[]) => void;
	required?: boolean;
}

export const Allocations = ({
	allocations = [],
	exchangeRates = [],
	idForm,
	onChange,
	onChangeExchangeRates,
	required = false,
}: AllocationsProps) => {
	// hooks
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);

	// state
	const [loans, setLoans] = useState<Loan[]>([]);

	// fetch loans
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=allocations,code,id,status`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// vars
	const allocationType = idForm.includes('category') ? 'categories' : 'components';
	const currencyReporting = project?.currencies?.find((c) => c.type === 'REPORTING')?.currency;
	const currenciesNonReporting = useMemo(
		() =>
			project?.currencies
				?.filter((c) => c.type !== 'REPORTING')
				.map((c) => c.currency)
				.sort() || [],
		[project?.currencies]
	);

	let totalLoans = 0;

	if (loans.length) {
		totalLoans = loans.filter((loan) => loan.status !== 'DISABLED').length;
	}

	const linkProjectSettings = (
		<Link component={RouterLink} to={`/project/${project?.idWbg}/settings/project`} />
	);

	// helper to get remaining amount for a loan
	const getRemainingForLoan = (loanId: number | null, type: 'categories' | 'components') => {
		if (!loanId) return null;
		const loan = loans.find((l) => l.id === loanId);
		if (!loan?.allocations?.summary) return null;
		return loan.allocations.summary[type].remaining;
	};

	// options for loan select dropdown
	const getOptionsLoans = (disabledLoanIds: number[]) => {
		return loans
			.map((loan) => ({
				disabled: disabledLoanIds.includes(loan.id) || loan.status === 'DISABLED',
				label: loan.code,
				value: loan.id,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	};

	// Transform allocations into row format for rendering
	const rows: AllocationRow[] = useMemo(() => {
		if (!allocations.length) {
			return [
				{
					amount: 0,
					conversions: exchangeRates.map((rate) => ({
						amount: 0,
						currency: rate.currency,
						dateExchangeRate: rate.dateExchangeRate,
						exchangeRate: rate.exchangeRate,
						tax: 0,
					})),
					currency: currencyReporting || '',
					loan: null,
				},
			];
		}

		// Group allocations by loan
		// Allocations with loan=null are pending and NOT grouped together
		const allocationsByLoan = new Map<number | string, AllocationPayload[]>();
		allocations.forEach((alloc, index) => {
			// Use a unique key for null loans (each gets its own row)
			const key = alloc.loan === null ? `null-${index}` : alloc.loan;
			if (!allocationsByLoan.has(key)) {
				allocationsByLoan.set(key, []);
			}
			const loanAllocations = allocationsByLoan.get(key);
			if (loanAllocations) {
				loanAllocations.push(alloc);
			}
		});

		return Array.from(allocationsByLoan.entries()).map(([key, loanAllocations]) => {
			const reportingAllocation = loanAllocations.find((a) => a.currency === currencyReporting);

			// Extract the actual loan value
			const loan = typeof key === 'string' && key.startsWith('null-') ? null : (key as number);

			let allocationLoan: number | null = loan;
			if (loan !== null && typeof loan === 'object') {
				allocationLoan = (loan as Loan).id;
			}

			const allocationAmount = reportingAllocation?.amount || 0;

			// Calculate conversions based on current exchange rates and allocation amount
			const conversions: ConversionPayload[] = exchangeRates.map((rate) => {
				const existingConversion = reportingAllocation?.conversions?.find(
					(conv) => conv.currency === rate.currency
				);

				const calculatedAmount =
					rate.exchangeRate > 0 ? Number((allocationAmount / rate.exchangeRate).toFixed(2)) : 0;

				return {
					amount: calculatedAmount,
					currency: rate.currency,
					dateExchangeRate: rate.dateExchangeRate,
					exchangeRate: rate.exchangeRate,
					tax: existingConversion?.tax || 0,
				};
			});

			return {
				amount: allocationAmount,
				conversions,
				currency: currencyReporting || '',
				loan: allocationLoan,
			};
		});
	}, [allocations, exchangeRates, currencyReporting]);

	// helpers
	const rowsToAllocations = (rowsUpdated: AllocationRow[]) => {
		const allocationsNew: (CategoryAllocationPayloadPending | ComponentAllocationPayloadPending)[] =
			[];

		rowsUpdated.forEach((row) => {
			// Include all rows, even without loan selected (for temporary state)
			allocationsNew.push({
				amount: row.amount,
				conversions: row.conversions,
				currency: row.currency,
				loan: row.loan || null, // Use null as placeholder for no loan selected
			});
		});

		return allocationsNew;
	};

	// handlers
	const handleAddRow = () => {
		const rowNew: AllocationRow = {
			amount: 0,
			conversions: exchangeRates.map((rate) => ({
				amount: 0,
				currency: rate.currency,
				dateExchangeRate: rate.dateExchangeRate,
				exchangeRate: rate.exchangeRate,
				tax: 0,
			})),
			currency: currencyReporting || '',
			loan: null,
		};
		const rowsUpdated = [...rows, rowNew];
		onChange(rowsToAllocations(rowsUpdated));
	};

	const handleRemoveRow = (index: number) => {
		// Prevent removing if it's the only row
		if (rows.length <= 1) {
			return;
		}

		const rowsUpdated = rows.filter((_, i) => i !== index);
		onChange(rowsToAllocations(rowsUpdated));
	};

	const handleLoanChange = (index: number, newLoanId: number) => {
		const rowsUpdated = rows.map((row, i) => (i === index ? { ...row, loan: newLoanId } : row));
		onChange(rowsToAllocations(rowsUpdated));
	};

	const handleAmountChange = (index: number, amount: string) => {
		const numAmount = amount === '' ? 0 : Number(amount) || 0;

		const rowsUpdated = rows.map((row, i) => {
			if (i === index) {
				// Update conversions based on current exchange rates
				const updatedConversions = exchangeRates.map((rate) => ({
					amount: rate.exchangeRate > 0 ? Number((numAmount / rate.exchangeRate).toFixed(2)) : 0,
					currency: rate.currency,
					dateExchangeRate: rate.dateExchangeRate,
					exchangeRate: rate.exchangeRate,
					tax: 0,
				}));
				return { ...row, amount: numAmount, conversions: updatedConversions };
			}
			return row;
		});

		onChange(rowsToAllocations(rowsUpdated));
	};

	const handleExchangeRateChange = (
		conversionIndex: number,
		name: string,
		value: string | number
	) => {
		const updatedRates = exchangeRates.map((rate, index) => {
			if (index === conversionIndex) {
				const newValue = name === 'exchangeRate' ? Number(value) : value;
				return {
					...rate,
					[name]: newValue,
				};
			}
			return rate;
		});

		onChangeExchangeRates(updatedRates);
	};

	let content = (
		<Grid item xs={12}>
			<Alert severity="warning">
				<Trans
					components={{
						a: linkProjectSettings,
					}}
					i18nKey="alert.reportingCurrencyRequired"
				/>
			</Alert>
		</Grid>
	);

	if (currencyReporting) {
		content = (
			<>
				{currenciesNonReporting.length > 0 && (
					<Grid item xs={12}>
						<ExchangeRates
							BoxProps={{
								sx: {
									border: 'none',
									padding: theme.spacing(0),
								},
							}}
							currencyBase={currencyReporting}
							exchangeRates={exchangeRates}
							hasAlert={false}
							hasLabel={false}
							onChange={handleExchangeRateChange}
							required={required}
						/>
					</Grid>
				)}
				{rows.map((row, index) => {
					// Disable loans already selected in other rows
					const disabledLoanIds = rows
						.filter((r) => r.loan !== row.loan)
						.map((r) => r.loan) as number[];

					let remainingAmount: number | null = null;
					let remainingText: string | null = null;
					let remainingTextColor = 'text.secondary';

					if (row.loan) {
						remainingAmount = getRemainingForLoan(row.loan, allocationType);

						if (remainingAmount !== null) {
							const isNegative = remainingAmount < 0;

							const remainingAmountFormatted = formatCurrency({
								currency: currencyReporting,
								notation: 'compact',
								value: isNegative ? Math.abs(remainingAmount) : remainingAmount,
							});

							if (isNegative) {
								remainingText = t('amountOverAllocated', { amount: remainingAmountFormatted });
								remainingTextColor = 'error.main';
							} else {
								remainingText = t('amountNotYetAllocated', { amount: remainingAmountFormatted });
							}
						}
					}

					return (
						<Grid item key={`row-${index}`} xs={12}>
							<Grid container>
								<Grid item xs={12}>
									<Grid alignItems="flex-end" container spacing={2}>
										<Grid item md={3.5} xs={12}>
											<Select
												disabled={!loans.length}
												id={`${idForm}-allocation-loan-${index}`}
												label={index === 0 ? t('financingSource') : undefined}
												name={`allocation-loan-${index}`}
												onChange={(e) => handleLoanChange(index, Number(e.target.value))}
												options={getOptionsLoans(disabledLoanIds)}
												required={required}
												value={row.loan || ''}
											/>
										</Grid>
										<Grid item md={true} xs={12}>
											<Input
												currency={currencyReporting}
												id={`${idForm}-allocation-amount-${index}`}
												label={index === 0 ? t('amount') : undefined}
												name={`allocation-amount-${index}`}
												onChange={(e) => handleAmountChange(index, e.target.value)}
												required={required}
												type="number"
												value={String(row.amount)}
											/>
										</Grid>
										{exchangeRates.map((rate, indexConversion) => {
											const conversion = row.conversions.find((c) => c.currency === rate.currency);
											return (
												<Grid
													item
													key={`${index}-${rate.currency}-${indexConversion}`}
													md={true}
													xs={12}>
													<Input
														currency={rate.currency}
														id={`${idForm}-allocation-conversion-${index}-${rate.currency}`}
														InputProps={{
															readOnly: true,
															startAdornment: <InputAdornment position="start">=</InputAdornment>,
														}}
														name={`allocation-conversion-${index}-${rate.currency}`}
														type="number"
														value={String(conversion?.amount || 0)}
													/>
												</Grid>
											);
										})}
										<Grid item md="auto" xs={12}>
											<Button
												aria-label={t('remove')}
												className="icon"
												color="error"
												disabled={rows.length === 1}
												onClick={() => handleRemoveRow(index)}
												sx={{ width: { md: 'auto', xs: '100%' } }}
												variant="outlined">
												<IconDelete />
											</Button>
										</Grid>
									</Grid>
								</Grid>
								{remainingText && (
									<Grid item xs={12}>
										<Typography color={remainingTextColor} variant="caption">
											{remainingText}
										</Typography>
									</Grid>
								)}
							</Grid>
						</Grid>
					);
				})}
				<Grid item xs={12}>
					<Button
						disabled={rows.length >= totalLoans}
						fullWidth
						onClick={handleAddRow}
						startIcon={<IconAdd />}
						variant="outlined">
						{t('addAllocation')}
					</Button>
				</Grid>
			</>
		);
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<InputLabel>{t('allocations')}</InputLabel>
				<Box
					sx={{
						borderColor: theme.palette.divider,
						borderStyle: 'solid',
						borderRadius: 2,
						borderWidth: 1,
					}}>
					<Grid container spacing={2} sx={{ padding: theme.spacing(2) }}>
						{content}
					</Grid>
				</Box>
			</Grid>
			{currencyReporting && (
				<Grid item xs={12}>
					<Alert severity="info">
						<Trans
							components={{
								a: linkProjectSettings,
							}}
							i18nKey="alert.allocations"
						/>
					</Alert>
				</Grid>
			)}
		</Grid>
	);
};
